import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { MapPin, Play, ArrowRight } from 'lucide-react';
import { RichText } from '../RichText';
/**
 * PreviewModal renders an item using the same visual language as the
 * public website, so the admin sees exactly what will be published.
 */
export const PreviewModal = ({ kind, item, onClose }) => {
  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="outline">Preview</Badge>
            <span className="text-base text-gray-600 capitalize">{kind}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="pt-2" data-testid="preview-content">
          {kind === 'project' && (
            <Card className="border-gray-200 hover:border-[#D4AF37]/50">
              <CardContent className="p-6">
                <div className="text-4xl mb-4">{item.icon}</div>
                <Badge className="mb-3 bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-xs">
                  {item.badge}
                </Badge>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">{item.description}</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </CardContent>
            </Card>
          )}

          {kind === 'event' && (
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#C19A2E] rounded-lg flex flex-col items-center justify-center text-white flex-shrink-0">
                    <div className="text-2xl font-bold">{item.date?.day}</div>
                    <div className="text-xs uppercase">{item.date?.month}</div>
                    <div className="text-xs">{item.date?.year}</div>
                  </div>
                  <div className="flex-1">
                    <Badge
                      className={`mb-2 text-xs ${
                        item.status === 'upcoming'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.status}
                    </Badge>
                    <h3 className="text-xl font-semibold text-[#0A1628] mb-2">{item.title}</h3>
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{item.location}</span>
                    </div>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {kind === 'video' && (
            <Card className="border-gray-200">
              <CardContent className="p-0">
                <div className="relative aspect-video bg-gray-100 rounded-t-lg overflow-hidden">
                  {item.thumbnail && (
                    <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-[#0A1628]/40 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center">
                      <Play className="w-8 h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                  {item.duration && (
                    <div className="absolute top-4 right-4 bg-[#0A1628]/80 text-white text-xs px-2 py-1 rounded">
                      {item.duration}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {item.type && (
                    <Badge className="mb-3 bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20 text-xs">
                      {item.type}
                    </Badge>
                  )}
                  <h4 className="text-lg font-semibold text-[#0A1628] mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {kind === 'gallery' && (
            <div className="relative">
              <img src={item.url} alt={item.title} className="w-full rounded-lg" />
              <div className="mt-3">
                <Badge className="mr-2 bg-[#D4AF37] text-white border-0 text-xs">{item.category}</Badge>
                <span className="text-sm text-gray-700">{item.title}</span>
              </div>
            </div>
          )}

          {kind === 'career' && (
            <Card className="border-gray-200">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0A1628] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 mb-3">
                  {[item.department, item.location, item.type].filter(Boolean).join(' • ')}
                </p>
                <RichText html={item.description} className="text-gray-600 mb-4" plainClass="text-gray-600 mb-4" />
                {item.responsibilities?.length > 0 && (
                  <>
                    <h4 className="font-semibold text-sm text-[#0A1628] mb-2">Responsibilities</h4>
                    <ul className="list-disc pl-5 text-sm text-gray-700 mb-3">
                      {item.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </>
                )}
                {item.requirements?.length > 0 && (
                  <>
                    <h4 className="font-semibold text-sm text-[#0A1628] mb-2">Requirements</h4>
                    <ul className="list-disc pl-5 text-sm text-gray-700">
                      {item.requirements.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <div className="mt-4 text-xs text-gray-500">
            {item.published === false ? '🚫 Currently a Draft — toggle "Publish" to show on site.' : '✅ Currently Live on the public site.'}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
